import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import { useScheduleRun } from '../hooks/useScheduleRun';
import type { StatusTone } from '@ui-kit';
import type { SchedulerRunStatus } from '../types/schedulerMonitor';

interface SchedulerRunDetailDrawerProps {
  runId: number | null;
  onClose: () => void;
}

function runStatusTone(status: SchedulerRunStatus): StatusTone {
  if (status === 'SUCCESS') return 'success';
  if (status === 'FAILED') return 'danger';
  return 'neutral';
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
}

/** Skeleton 행 */
function SkeletonField() {
  return (
    <div
      style={{
        height: 16,
        width: '60%',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--color-surface-elevated)',
        animation: 'pulse 1.5s ease-in-out infinite',
        marginBottom: 'var(--spacing-2)',
      }}
    />
  );
}

/**
 * SchedulerRunDetailDrawer — 스케줄 실행 1건 상세 패널 (drawer).
 * role=dialog, aria-modal=true, ESC 닫기, focus trap.
 */
export function SchedulerRunDetailDrawer({
  runId,
  onClose,
}: SchedulerRunDetailDrawerProps) {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const { data, isLoading, isError, error, refetch } = useScheduleRun(runId);

  // ESC 키 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // 열릴 때 닫기 버튼 포커스
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  if (runId === null) return null;

  return (
    <>
      {/* 오버레이 */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 900,
          background: 'rgba(0,0,0,0.35)',
        }}
      />

      {/* 드로어 패널 */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="run-drawer-title"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(40%, 480px)',
          minWidth: 320,
          zIndex: 901,
          background: 'var(--color-surface-background)',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--spacing-4) var(--spacing-5)',
            borderBottom: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
          }}
        >
          <h2
            id="run-drawer-title"
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            {t('schedulerMonitor.detail.run.title')}
            {data ? ` #${data.id}` : ''}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t('schedulerMonitor.detail.close')}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-text-secondary)',
              lineHeight: 1,
              padding: 'var(--spacing-1)',
            }}
          >
            ✕
          </button>
        </div>

        {/* 본문 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-5)' }}>
          {/* loading */}
          {isLoading && (
            <div aria-busy="true" aria-label={t('schedulerMonitor.runs.loadingAriaLabel')}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{ marginBottom: 'var(--spacing-4)' }}>
                  <div
                    style={{
                      height: 10,
                      width: '35%',
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--color-surface-elevated)',
                      marginBottom: 'var(--spacing-1)',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  />
                  <SkeletonField />
                </div>
              ))}
            </div>
          )}

          {/* error */}
          {isError && (
            <div
              style={{
                padding: 'var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-danger)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <p>{(error as { message?: string })?.message ?? t('schedulerMonitor.error.loadFailed')}</p>
              <button
                type="button"
                onClick={() => void refetch()}
                style={{
                  marginTop: 'var(--spacing-2)',
                  padding: 'var(--spacing-1) var(--spacing-3)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: 'pointer',
                }}
              >
                {t('schedulerMonitor.error.retry')}
              </button>
            </div>
          )}

          {/* default */}
          {data && (
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: '140px 1fr',
                gap: 'var(--spacing-3) var(--spacing-4)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600, alignSelf: 'start' }}>
                {t('schedulerMonitor.detail.field.taskName')}
              </dt>
              <dd style={{ margin: 0, fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-primary)' }}>
                {data.taskName}
              </dd>

              <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600, alignSelf: 'start' }}>
                {t('schedulerMonitor.detail.field.status')}
              </dt>
              <dd style={{ margin: 0 }}>
                <StatusPill tone={runStatusTone(data.status)}>
                  {t(`schedulerMonitor.status.${data.status}`)}
                </StatusPill>
              </dd>

              <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                {t('schedulerMonitor.detail.field.startTime')}
              </dt>
              <dd style={{ margin: 0, color: 'var(--color-text-primary)' }}>
                {formatDate(data.startedAt)}
              </dd>

              <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                {t('schedulerMonitor.detail.field.endTime')}
              </dt>
              <dd style={{ margin: 0, color: 'var(--color-text-primary)' }}>
                {formatDate(data.finishedAt)}
              </dd>

              <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                {t('schedulerMonitor.detail.field.duration')}
              </dt>
              <dd style={{ margin: 0, color: 'var(--color-text-primary)' }}>
                {data.durationMs != null ? `${data.durationMs} ms` : '-'}
              </dd>

              <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                {t('schedulerMonitor.detail.field.hostname')}
              </dt>
              <dd style={{ margin: 0, fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-secondary)' }}>
                {data.hostname}
              </dd>

              {data.errorSummary && (
                <>
                  <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600, alignSelf: 'start' }}>
                    {t('schedulerMonitor.detail.field.errorSummary')}
                  </dt>
                  <dd
                    style={{
                      margin: 0,
                      color: 'var(--color-danger)',
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 'var(--font-size-xs)',
                      wordBreak: 'break-all',
                    }}
                  >
                    {data.errorSummary}
                  </dd>
                </>
              )}
            </dl>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}
